import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  FileUploaderDropContainer,
  FileUploaderItem,
  InlineLoading,
  InlineNotification,
  RadioButton,
  RadioButtonGroup,
  Stack,
  TextArea,
  TextInput,
  Tile,
} from "@carbon/react";
import type { ImportFormatResponse, ImportProbeResponse } from "../../types";
import SavedFormatNotice from "./SavedFormatNotice";

/** Only .xlsx, matching what the backend will accept. */
const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** What the backend will take. Checked here too, so an oversized file fails before it is sent. */
const MAX_BYTES = 15 * 1024 * 1024;

/** The shape of a Google Sheets link, as an example rather than as copy — the same in every language. */
const SHEET_URL_EXAMPLE = "https://docs.google.com/spreadsheets/d/...";

type Props = {
  formats: ImportFormatResponse[];
  isStarting: boolean;
  isProbing: boolean;
  probe: ImportProbeResponse | null;
  onFileChosen: (file: File | null) => void;
  onStart: (file: File, description: string) => void;
  onStartSheet: (
    spreadsheetUrl: string,
    sheetName: string,
    description: string
  ) => void;
};

/** Where the rows are. Everything after this step is the same either way. */
type Source = "file" | "sheet";

/**
 * Choosing a file, and being asked about it only when it is not one we know.
 *
 * The file is looked at the moment it is chosen. If the company has imported one with these
 * columns before, everything — the mapping, the value decisions, the description somebody wrote
 * the first time — is already saved, and there is nothing here to fill in. Only an unfamiliar
 * file gets the questions.
 */
const UploadStep = ({
  formats,
  isStarting,
  isProbing,
  probe,
  onFileChosen,
  onStart,
  onStartSheet,
}: Props) => {
  const { t } = useTranslation();
  const [source, setSource] = useState<Source>("file");
  const [spreadsheetUrl, setSpreadsheetUrl] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  /**
   * The five lines that decide whether a mapping comes out right.
   *
   * Offered as a template rather than an empty box because "describe your file" produces nothing
   * useful, while a sentence with a blank in it produces the one fact the file cannot tell us.
   * Only ever shown for a file nobody has described before.
   */
  const [description, setDescription] = useState(() =>
    t("purchaseRequests.import.descriptionTemplate")
  );
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognised = probe?.match ?? null;
  const nearMatch = probe?.nearMatches?.[0] ?? null;

  const refuse = (message: string) => {
    setError(message);
    setFile(null);
    onFileChosen(null);
  };

  const chooseFile = (chosen: File | null) => {
    setError(null);
    if (!chosen) return;
    const looksRight =
      chosen.type === XLSX_MIME || chosen.name.toLowerCase().endsWith(".xlsx");
    if (!looksRight) {
      refuse(t("purchaseRequests.import.onlyXlsx"));
      return;
    }
    if (chosen.size > MAX_BYTES) {
      refuse(t("purchaseRequests.import.tooBig"));
      return;
    }
    setFile(chosen);
    onFileChosen(chosen);
  };

  /**
   * Forgets the chosen file. Carbon's drop container clears its own input on every click, so
   * choosing the same file again still fires a change.
   */
  const clearFile = () => {
    setFile(null);
    setError(null);
    onFileChosen(null);
  };

  /**
   * A file that nearly matches a saved format borrows that format's description.
   *
   * It is what the same person wrote about the same report last month, and it beats the template
   * by a distance. Only while the box is untouched: once the buyer has typed, what they typed is
   * the description, and replacing it because a probe came back late would be maddening.
   */
  const savedDescription =
    probe?.match?.userDescription ?? nearMatch?.userDescription ?? null;
  useEffect(() => {
    if (descriptionTouched || !savedDescription) return;
    setDescription(savedDescription);
  }, [savedDescription, descriptionTouched]);

  const stillHasPlaceholders = /<[^>]+>/.test(description);
  const ready = source === "file" ? !!file : spreadsheetUrl.trim().length > 0;
  const askForDescription = source === "sheet" || !recognised;

  const start = () => {
    if (source === "file" && file) {
      onStart(file, recognised ? "" : description);
    } else if (source === "sheet") {
      onStartSheet(spreadsheetUrl.trim(), sheetName.trim(), description);
    }
  };

  return (
    <div className="pr-import__upload">
      <Tile className="pr-import__panel">
        <h3 className="pr-import__title">
          {t("purchaseRequests.import.chooseFile")}
        </h3>

        <RadioButtonGroup
          className="pr-import__source"
          name="pr-import-source"
          legendText={t("purchaseRequests.import.sourceLegend")}
          valueSelected={source}
          onChange={(value) => setSource(value as Source)}
        >
          <RadioButton
            id="pr-import-source-file"
            value="file"
            labelText={t("purchaseRequests.import.source.file")}
          />
          <RadioButton
            id="pr-import-source-sheet"
            value="sheet"
            labelText={t("purchaseRequests.import.source.sheet")}
          />
        </RadioButtonGroup>

        {source === "sheet" && (
          <Stack gap={5}>
            <TextInput
              id="pr-import-sheet-url"
              labelText={t("purchaseRequests.import.sheetUrlLabel")}
              placeholder={SHEET_URL_EXAMPLE}
              value={spreadsheetUrl}
              onChange={(event) => setSpreadsheetUrl(event.target.value)}
            />
            <TextInput
              id="pr-import-sheet-name"
              labelText={t("purchaseRequests.import.sheetNameLabel")}
              placeholder={t("purchaseRequests.import.sheetNameHint")}
              helperText={t("purchaseRequests.import.sheetShareHint")}
              value={sheetName}
              onChange={(event) => setSheetName(event.target.value)}
            />
          </Stack>
        )}

        {source === "file" && (
          <Stack gap={3}>
            <FileUploaderDropContainer
              className="pr-import__dropzone"
              accept={[".xlsx"]}
              labelText={t("purchaseRequests.import.dropHere")}
              onAddFiles={(_event, { addedFiles }) =>
                chooseFile(addedFiles[0] ?? null)
              }
            />
            <p className="pr-import__hint">
              {t("purchaseRequests.import.accepted")}
            </p>
            {file && (
              <FileUploaderItem
                name={file.name}
                status="edit"
                iconDescription={t("cta.delete")}
                onDelete={clearFile}
              />
            )}
            {isProbing && (
              <InlineLoading
                description={t("purchaseRequests.import.checkingIfKnown")}
              />
            )}
          </Stack>
        )}

        {error && <p className="pr-import__error-text">{error}</p>}

        <p className="pr-import__hint">
          {t("purchaseRequests.import.privacyNote")}
        </p>

        {formats.length > 0 && !probe && (
          <p className="pr-import__hint">
            {t("purchaseRequests.import.savedFormatsHint")}
          </p>
        )}
      </Tile>

      <Tile className="pr-import__panel">
        {source === "file" && recognised && (
          <SavedFormatNotice format={recognised} />
        )}

        {askForDescription && (
          <>
            <h3 className="pr-import__title">
              {t("purchaseRequests.import.describeFile")}
            </h3>

            {nearMatch ? (
              <InlineNotification
                kind="warning"
                lowContrast
                hideCloseButton
                className="pr-import__notification"
                title={t("purchaseRequests.import.nearMatch", {
                  name: nearMatch.name,
                  count: nearMatch.missingColumns.length,
                  columns: nearMatch.missingColumns.join(", "),
                })}
              />
            ) : (
              <p className="pr-import__hint">
                {t("purchaseRequests.import.describeHint")}
              </p>
            )}

            <TextArea
              id="pr-import-description"
              className="pr-import__description"
              labelText={t("purchaseRequests.import.descriptionLabel")}
              hideLabel
              rows={12}
              value={description}
              onChange={(event) => {
                setDescription(event.target.value);
                setDescriptionTouched(true);
              }}
            />

            {stillHasPlaceholders && (
              <InlineNotification
                kind="warning"
                lowContrast
                hideCloseButton
                className="pr-import__notification"
                title={t("purchaseRequests.import.placeholdersLeft")}
              />
            )}
          </>
        )}

        <div className="pr-import__actions">
          {isStarting ? (
            <InlineLoading description={t("purchaseRequests.import.reading")} />
          ) : (
            <Button onClick={start} disabled={!ready || isProbing}>
              {recognised
                ? t("purchaseRequests.import.continueWithFormat")
                : t("purchaseRequests.import.proposeMapping")}
            </Button>
          )}
        </div>
      </Tile>
    </div>
  );
};

export default UploadStep;
